import { CreateLoaderTable } from "@/components/Loaders";
import { Main } from "@/components/Main";
import { useEffect, useState, useCallback } from "react";
import { ApiDataProps } from "../Tenants";
import { TypePageActions } from "../Plans";
import { AddBtn } from "@/App/Clients/Add";
import { TableData } from "@/App/Clients/Table";
import { getClientsDataWithFilters } from "@/Api/clients";
import { ClientsFiltersComponent, ClientsFilters } from "@/components/ClientsFilters";
import { getSelectClientOrigins } from "@/Api/clientOrigins";
import { getSelectClientCategories } from "@/Api/clientCategories";
import { usePermissions } from "@/hooks/usePermissions";
import { If } from "@/components/If";
import { useAuth } from "@/hooks/auth";
import { Users } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";

export type TableDataProps = {
  id: number;
  uuid: string;
  name: string;
  document: string;
  phone: string;
  phone_2?: string | null;
  image: string | null;
  email: string;
  discount: number;
  discount_type_id: number;
  client_origin_id: number;
  client_category_id: number;
  address_id: number | null;
  is_active: boolean;
  is_new?: boolean;
  tenant_id: number;
  created_at: string;
  addresses: any;
  updated_at: string;
  deleted_at: string | null;
  total_balance?: number;
  has_purchase_voucher?: boolean;
  max_purchase_voucher_value?: number;
  purchase_voucher_balance?: number;
  discount_type: {
    id: number;
    name: string;
  };
  client_category: {
    id: number;
    name: string;
    establishment_id: number;
  };
  client_origin: {
    id: number;
    name: string;
    establishment_id: number;
  };
  client_origin_photo?: string | null;
  address: {
    id: number;
    street: string;
    number: string;
    complement: string;
    neighborhood: string;
    city: string;
    state: string;
    zip_code: string;
    latitude: number | null;
    longitude: number | null;
    created_at: string;
    updated_at: string;
  };
};

export const PageList = () => {
  const [listData, setListData] = useState({} as ApiDataProps<TableDataProps>);
  const [state, setState] = useState("loading");
  const [currentFilters, setCurrentFilters] = useState<ClientsFilters>({
    order_by: 'created_at',
    order_direction: 'desc'
  });
  const [sortConfig, setSortConfig] = useState<{
    column: string;
    direction: "asc" | "desc";
  }>({ column: "created_at", direction: "desc" });
  const [origins, setOrigins] = useState<Array<{ id: number; name: string }>>([]);
  const [categories, setCategories] = useState<Array<{ id: number; name: string }>>([]);
  const { hasPermission } = usePermissions();
  const user = useAuth();
  const sellerId = user?.user?.seller?.id || "";

  const fetchData = useCallback(async (page: number = 1, filters: ClientsFilters = currentFilters) => {
    setState("loading");
    const { data } = await getClientsDataWithFilters({
      ...filters,
      page,
      seller_id: sellerId,
    });
    setListData(data?.response);
    setState("production");
  }, [sellerId, currentFilters]);

  useEffect(() => {
    fetchData(1);
    // Load filter options
    const fetchOptions = async () => {
      try {
        const [originsRes, categoriesRes] = await Promise.all([
          getSelectClientOrigins(),
          getSelectClientCategories(),
        ]);
        setOrigins(originsRes.data?.response || []);
        setCategories(categoriesRes.data?.response || []);
      } catch (error) {
        console.error("Error fetching filter options:", error);
      }
    };
    fetchOptions();
  }, []);

  const handleFiltersChange = (filters: ClientsFilters) => {
    setCurrentFilters(filters);
  };

  const handleApplyFilters = async () => {
    setSortConfig({ column: currentFilters.order_by || 'created_at', direction: currentFilters.order_direction || 'desc' });
    await fetchData(1, currentFilters);
  };

  const handleClearFilters = async () => {
    const clearedFilters: ClientsFilters = {
      order_by: 'created_at',
      order_direction: 'desc'
    };
    setCurrentFilters(clearedFilters);
    setSortConfig({ column: "created_at", direction: "desc" });
    await fetchData(1, clearedFilters);
  };

  const handleSort = async (column: string, direction: "asc" | "desc") => {
    setSortConfig({ column, direction });
    const newFilters = {
      ...currentFilters,
      order_by: column as 'name' | 'total_balance' | 'created_at',
      order_direction: direction
    };
    setCurrentFilters(newFilters);
    await fetchData(1, newFilters);
  };

  const pageActions: TypePageActions & { refresh: () => Promise<void> } = {
    refresh: async () => {
      await fetchData(listData?.current_page || 1);
    },
    next: async () => {
      if (listData?.current_page < listData?.last_page) {
        await fetchData(listData?.current_page + 1);
      }
    },
    previous: async () => {
      if (listData?.current_page > 1) {
        await fetchData(listData?.current_page - 1);
      }
    },
    lastPage: async () => {
      if (listData?.current_page !== listData?.last_page) {
        await fetchData(listData?.last_page);
      }
    },
  };

  return (
    <Main>
      <If condition={hasPermission("view-clients-list")}>
        <PageHeader
          icon={Users}
          title="Clientes"
          description="Gerencie seus clientes"
        >
          <If condition={hasPermission("create-client")}>
            <AddBtn updateFront={setListData} />
          </If>
        </PageHeader>
        <div className="flex flex-col gap-3">
          <ClientsFiltersComponent
            filters={currentFilters}
            onFiltersChange={handleFiltersChange}
            onApplyFilters={handleApplyFilters}
            onClearFilters={handleClearFilters}
            origins={origins}
            categories={categories}
          />
          {state === "loading" ? (
            <CreateLoaderTable cols={4} lines={6} />
          ) : (
            <TableData
              dataApi={listData}
              pageActions={pageActions}
              updateFront={setListData}
              onSort={handleSort}
              sortConfig={sortConfig}
              showDiscountColumn={currentFilters.has_discount === true}
            />
          )}
        </div>
      </If>
    </Main>
  );
};
